export interface ClosedDealSnapshot {
  projectId: string;
  amount: number;
  acceptedAt: string;
  ownerIds: string[];
}

export interface SalesDisplaySnapshot {
  closedDeals: ClosedDealSnapshot[];
  achievers: { id: string; name: string; memberIds: string[] }[];
}

export interface SalesDisplayNotification {
  id: string;
  kind: "sale" | "record" | "goal" | "birthday";
  title: string;
  message: string;
  people: { id: string; name: string; imageUrl: string | null }[];
  durationSeconds: number;
}

export const CURATED_SALES_DISPLAY_PEOPLE = [
  { firstName: "William", imageUrl: "/sales-display/people/william.webp" },
  { firstName: "Tobias", imageUrl: "/sales-display/people/tobias.webp" },
  { firstName: "Tiril", imageUrl: "/sales-display/people/tiril.webp" },
  { firstName: "Terje", imageUrl: "/sales-display/people/terje.webp" },
  { firstName: "Helge", imageUrl: "/sales-display/people/helge.webp" },
  { firstName: "Calle", imageUrl: "/sales-display/people/calle.webp" },
  { firstName: "Mariann", imageUrl: "/sales-display/people/mariann.webp" },
  { firstName: "Sigurd", imageUrl: "/sales-display/people/sigurd.webp" },
  { firstName: "Christer", imageUrl: "/sales-display/people/christer.webp" },
  { firstName: "Daniel", imageUrl: "/sales-display/people/daniel.webp" },
] as const;

const CURATED_IMAGES: Record<string, string> = Object.fromEntries(
  CURATED_SALES_DISPLAY_PEOPLE.map((person) => [
    person.firstName.toLocaleLowerCase("nb-NO"),
    person.imageUrl,
  ]),
);

export function curatedSalesPersonImage(name: string): string | null {
  const firstName = name.trim().split(/\s+/)[0]?.toLocaleLowerCase("nb-NO") ?? "";
  return CURATED_IMAGES[firstName] ?? null;
}

interface DisplayOwner {
  project_id: string;
  user_id: string;
  sales_unit_id?: string | null;
}

interface DisplaySalesUnit {
  id: string;
  memberUserIds: string[];
}

interface DisplayPreviewPerson {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

interface DisplayPreviewSalesUnit extends DisplaySalesUnit {
  name?: string;
}

/**
 * Complete review loop for every sales unit and every curated employee image.
 * This is intentionally separate from live events, so production never shows
 * example sales unless preview mode is explicitly enabled in the URL.
 */
export function salesDisplayPreviewNotifications(
  people: DisplayPreviewPerson[],
  salesUnits: DisplayPreviewSalesUnit[],
): SalesDisplayNotification[] {
  const peopleById = new Map(people.map((person) => [person.userId, person]));
  const personByFirstName = new Map(
    people.map((person) => [
      person.name.trim().split(/\s+/)[0]?.toLocaleLowerCase("nb-NO") ?? "",
      person,
    ]),
  );

  const salesSlides = salesUnits.map((unit) => ({
    id: `preview-sale-${unit.id}`,
    kind: "sale" as const,
    title: "Nytt salg!",
    message: unit.name ?? "Selger",
    people: unit.memberUserIds.map((userId) => {
      const person = peopleById.get(userId);
      const name = person?.name ?? unit.name ?? "Selger";
      return {
        id: userId,
        name,
        imageUrl: curatedSalesPersonImage(name) ?? person?.avatarUrl ?? null,
      };
    }),
    durationSeconds: 15,
  }));

  const birthdaySlides = CURATED_SALES_DISPLAY_PEOPLE.map((curated) => {
    const person = personByFirstName.get(curated.firstName.toLocaleLowerCase("nb-NO"));
    const name = person?.name ?? curated.firstName;
    return {
      id: `preview-birthday-${curated.firstName.toLocaleLowerCase("nb-NO")}`,
      kind: "birthday" as const,
      title: "Gratulerer med dagen!",
      message: name,
      people: [{
        id: person?.userId ?? `curated-${curated.firstName.toLocaleLowerCase("nb-NO")}`,
        name,
        imageUrl: curated.imageUrl,
      }],
      durationSeconds: 15,
    };
  });

  return [...salesSlides, ...birthdaySlides];
}

/**
 * A sales event represents the complete sales unit, not only the person rows
 * stored on the project. This keeps shared units such as Sigurd & Christer
 * together while preserving individually attributed owners.
 */
export function buildDisplayPeopleByProject(
  owners: DisplayOwner[],
  salesUnits: DisplaySalesUnit[],
): Map<string, string[]> {
  const membersByUnit = new Map(salesUnits.map((unit) => [unit.id, unit.memberUserIds]));
  const unitIdsByMember = new Map<string, string[]>();
  for (const unit of salesUnits) {
    for (const memberId of unit.memberUserIds) {
      unitIdsByMember.set(memberId, [...(unitIdsByMember.get(memberId) ?? []), unit.id]);
    }
  }
  const peopleByProject = new Map<string, Set<string>>();

  for (const owner of owners) {
    const people = peopleByProject.get(owner.project_id) ?? new Set<string>();
    people.add(owner.user_id);
    const unitIds = owner.sales_unit_id
      ? [owner.sales_unit_id]
      : unitIdsByMember.get(owner.user_id) ?? [];
    for (const unitId of unitIds) {
      for (const memberId of membersByUnit.get(unitId) ?? []) people.add(memberId);
    }
    peopleByProject.set(owner.project_id, people);
  }

  return new Map(
    [...peopleByProject].map(([projectId, people]) => [projectId, [...people]]),
  );
}

/** Hendelser siden forrige databilde. Første bilde etablerer kun baseline. */
export function detectSalesDisplayNotifications(
  previous: SalesDisplaySnapshot | null,
  next: SalesDisplaySnapshot,
  nameOf: (userId: string) => string,
  projectNameOf: (projectId: string) => string,
  avatarOf: (userId: string) => string | null = () => null,
): SalesDisplayNotification[] {
  if (!previous) return [];
  const notifications: SalesDisplayNotification[] = [];
  const previousDeals = new Set(previous.closedDeals.map((deal) => deal.projectId));
  const previousRecord = Math.max(0, ...previous.closedDeals.map((deal) => deal.amount));
  const newDeals = next.closedDeals
    .filter((deal) => !previousDeals.has(deal.projectId))
    .sort((a, b) => a.acceptedAt.localeCompare(b.acceptedAt));

  for (const deal of newDeals) {
    const isRecord = deal.amount > previousRecord;
    notifications.push({
      id: `${isRecord ? "record" : "sale"}-${deal.projectId}-${deal.acceptedAt}`,
      kind: isRecord ? "record" : "sale",
      title: isRecord ? "Ny salgsrekord!" : "Nytt salg!",
      message: projectNameOf(deal.projectId),
      people: deal.ownerIds.map((id) => {
        const name = nameOf(id);
        return { id, name, imageUrl: curatedSalesPersonImage(name) ?? avatarOf(id) };
      }),
      durationSeconds: 15,
    });
  }

  const previousAchievers = new Set(previous.achievers.map((achiever) => achiever.id));
  for (const achiever of next.achievers) {
    if (previousAchievers.has(achiever.id)) continue;
    notifications.push({
      id: `goal-${achiever.id}`,
      kind: "goal",
      title: "Målet er nådd!",
      message: `${achiever.name} har passert 100 % av sitt felles årsmål.`,
      people: achiever.memberIds.map((id) => {
        const name = nameOf(id);
        return { id, name, imageUrl: curatedSalesPersonImage(name) ?? avatarOf(id) };
      }),
      durationSeconds: 15,
    });
  }

  return notifications;
}

export function birthdayNotification(
  people: { id: string; full_name: string; avatar_url: string | null }[],
  dateKey: string,
): SalesDisplayNotification | null {
  if (people.length === 0) return null;
  return {
    id: `birthday-${dateKey}-${people.map((person) => person.id).sort().join("-")}`,
    kind: "birthday",
    title: people.length === 1 ? "Gratulerer med dagen!" : "Dagens bursdager!",
    message: people.map((person) => person.full_name).join(" og "),
    people: people.map((person) => ({
      id: person.id,
      name: person.full_name,
      imageUrl: curatedSalesPersonImage(person.full_name) ?? person.avatar_url,
    })),
    durationSeconds: 15,
  };
}
